"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useSession, signOut } from "next-auth/react";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils/general";
import { Role } from "@prisma/client";
// import NotificationBell from "./NotificationBell";
// import useNotificationSubscription from "@/hooks/useNotificationSubscription";
import ProfileImage from "@/components/ui/ProfileImage";
import Image from "next/image";
import { mainNavigation } from "@/data/navigation";
import { formatCurrency } from "@/lib/utils/general";
import useSWR from "swr";

type MenuType = "mobile" | "profile" | "notifications" | null;

type SessionUser = {
	id: string;
	name?: string | null;
	email?: string | null;
	image?: string | null;
	role: Role;
	provider: string;
	emailVerified?: Date | null;
	isBanned: boolean;
	banReason?: string | null;
	balance: number;
};

const Header = () => {
	const { data: session } = useSession();
	const pathname = usePathname();
	const [mounted, setMounted] = useState(false);
	const [activeMenu, setActiveMenu] = useState<MenuType>(null);
	const [isEdgeBrowser, setIsEdgeBrowser] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const mobileMenuRef = useRef<HTMLDivElement>(null);
	const mobileMenuContentRef = useRef<HTMLDivElement>(null);
	const profileMenuRef = useRef<HTMLDivElement>(null);
	const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);

	// Fetch live balance using SWR
	const { data: balanceData } = useSWR(
		session?.user ? `/api/users/${session.user.id}/balance` : null,
		async (url) => {
			const res = await fetch(url);
			if (!res.ok) throw new Error("Failed to fetch balance");
			return res.json();
		},
		{
			revalidateOnFocus: true, // Sayfa focus olduğunda güncelle
			refreshInterval: 15000, // Her 15 saniyede bir güncelle
		}
	);

	const balance =
		balanceData?.balance ?? (session?.user as SessionUser)?.balance ?? 0;

	// Hydration fix
	useEffect(() => {
		setMounted(true);

		// Detect Edge browser
		const isEdge = /Edg/.test(navigator.userAgent);
		setIsEdgeBrowser(isEdge);

		// Add scroll event listener
		const handleScroll = () => {
			setScrolled(window.scrollY > 10);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// Close menus when clicking outside
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			// Don't close if clicking the mobile menu button
			if (
				mobileMenuButtonRef.current &&
				mobileMenuButtonRef.current.contains(event.target as Node)
			) {
				return;
			}

			// Otherwise check if click is outside menu areas
			if (
				mobileMenuRef.current &&
				!mobileMenuRef.current.contains(event.target as Node) &&
				profileMenuRef.current &&
				!profileMenuRef.current.contains(event.target as Node)
			) {
				setActiveMenu(null);
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	// Control body scroll when menu is open
	useEffect(() => {
		if (activeMenu === "mobile") {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}

		return () => {
			document.body.style.overflow = "";
		};
	}, [activeMenu]);

	if (!mounted) {
		return null;
	}

	const isAdmin = session?.user?.role === Role.ADMIN;

	// Handle profile menu toggling
	const handleProfileMenuToggle = () => {
		setActiveMenu(activeMenu === "profile" ? null : "profile");
	};

	// Separate handler for mobile menu toggle button
	const handleMobileMenuToggle = () => {
		setActiveMenu(activeMenu === "mobile" ? null : "mobile");
	};

	// Function to close any open menu
	const closeMenu = () => {
		setActiveMenu(null);
	};

	// Handle backdrop click to close menu
	const handleBackdropClick = (e: React.MouseEvent) => {
		// Only close if clicking directly on the backdrop, not on the menu content
		if (
			mobileMenuContentRef.current &&
			!mobileMenuContentRef.current.contains(e.target as Node)
		) {
			closeMenu();
		}
	};

	return (
		<>
			<motion.header
				initial={{ opacity: 0, scale: 0.98 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{
					duration: 0.5,
					ease: "easeOut",
				}}
				className={cn(
					"sticky top-0 z-50 w-full transition-all duration-500",
					scrolled
						? "bg-purple-dark/80 backdrop-blur-md shadow-lg"
						: "bg-transparent"
				)}
			>
				{/* Decorative background elements */}
				<div className="absolute inset-0 overflow-hidden pointer-events-none">
					<div className="absolute top-0 left-1/4 w-32 h-32 rounded-full bg-primary/10 blur-3xl" />
					<div className="absolute bottom-0 right-1/4 w-32 h-32 rounded-full bg-purple-light/10 blur-3xl" />
				</div>

				<div className="relative flex h-20 items-center justify-between px-4 md:px-6 lg:px-8 z-10">
					{/* Logo - Always visible on both mobile and desktop */}
					<motion.div
						className="flex items-center"
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
					>
						<Link href="/" className="flex items-center gap-2">
							<Image
								unoptimized
								src="/images/logo.png"
								alt="Swendo Shop Logo"
								width={80}
								height={80}
								className="w-12 h-12 rounded-xl"
								priority
							/>

							<span className="text-xl font-bold bg-gradient-to-r from-primary via-purple-light to-primary bg-clip-text text-transparent">
								Swendo Shop
							</span>
						</Link>
					</motion.div>

					{/* Desktop Navigation - Updated with icons */}
					<nav className="hidden lg:flex lg:items-center gap-6 absolute left-1/2 transform -translate-x-1/2">
						{mainNavigation.map((item) => (
							<motion.div
								key={item.href}
								whileHover={{ y: -2 }}
								whileTap={{ scale: 0.95 }}
							>
								<Link
									href={item.href}
									className={cn(
										"relative px-2.5 py-1.5 transition-all duration-300 font-medium flex items-center gap-1.5 text-sm",
										pathname === item.href
											? "text-primary"
											: "text-white/80 hover:text-white"
									)}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
										strokeWidth={1.5}
										stroke="currentColor"
										className="w-4 h-4"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d={item.icon}
										/>
									</svg>
									{item.name}
									{pathname === item.href && (
										<motion.div
											layoutId="navbar-indicator"
											className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full"
											initial={false}
											transition={{
												type: "spring",
												stiffness: 500,
												damping: 30,
											}}
										/>
									)}
								</Link>
							</motion.div>
						))}
					</nav>

					<div className="flex flex-1 lg:flex-none items-center justify-end space-x-2">
						<div className="flex items-center gap-4">
							{session ? (
								<>
									{/* Profile button - Only visible on desktop */}
									<div
										className="relative hidden md:block"
										ref={profileMenuRef}
									>
										<motion.button
											whileHover={{ scale: 1.05 }}
											whileTap={{ scale: 0.95 }}
											onClick={handleProfileMenuToggle}
											className="flex items-center gap-2 rounded-full p-1.5 bg-purple-dark/50 border border-primary/20 hover:bg-primary/20 transition-colors"
										>
											<ProfileImage
												src={session.user.image || null}
												alt={session.user.name || "User"}
												size="sm"
											/>
										</motion.button>

										<AnimatePresence>
											{activeMenu === "profile" && (
												<motion.div
													initial={{ opacity: 0, scale: 0.95, y: 10 }}
													animate={{ opacity: 1, scale: 1, y: 0 }}
													exit={{ opacity: 0, scale: 0.95, y: 10 }}
													transition={{
														type: "spring",
														stiffness: 300,
														damping: 25,
													}}
													className={cn(
														"absolute right-0 mt-2 w-72 rounded-xl border border-primary/20 p-3 shadow-lg shadow-primary/10",
														isEdgeBrowser
															? "bg-purple-dark bg-slate-900/90"
															: "bg-purple-dark/95 backdrop-blur-xl"
													)}
												>
													<div className="flex items-center gap-4 border-b border-primary/20 p-3">
														<ProfileImage
															src={session.user.image || null}
															alt={session.user.name || "User"}
															size="md"
														/>
														<div className="flex flex-col">
															<span className="text-base font-semibold text-white">
																{session.user.name}
															</span>
															<span className="text-sm text-white/60">
																{session.user.email}
															</span>
														</div>
													</div>

													{/* Balance Display */}
													<div className="flex flex-col gap-1 px-3 py-2 border-b border-primary/20">
														<div className="flex items-center justify-between">
															<span className="text-xs text-white/60">
																Available Balance
															</span>
															<Link
																href="/dashboard/balance"
																className="text-xs text-primary hover:text-primary/80 transition-colors"
																onClick={closeMenu}
															>
																Add Balance
															</Link>
														</div>
														<div className="flex items-center gap-2">
															<div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
																<svg
																	xmlns="http://www.w3.org/2000/svg"
																	fill="none"
																	viewBox="0 0 24 24"
																	strokeWidth={1.5}
																	stroke="currentColor"
																	className="w-4 h-4 text-primary"
																>
																	<path
																		strokeLinecap="round"
																		strokeLinejoin="round"
																		d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
																	/>
																</svg>
															</div>
															<div className="flex flex-col">
																<span className="text-lg font-semibold text-white">
																	{formatCurrency(balance)}
																</span>
															</div>
														</div>
													</div>

													<div className="py-2 space-y-1">
														{[
															{
																href: "/dashboard",
																label: "Dashboard",
																icon: "M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25",
															},
															{
																href: "/dashboard/inventory",
																label: "My Inventory",
																icon: "M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75m16.5 0v3.75m16.5 0v3.75m16.5 0v3.75m16.5 0v3.75m16.5 0v3.75m16.5 0v3.75m16.5 0v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125",
															},
															{
																href: "/dashboard/balance",
																label: "Add Balance",
																icon: "M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
															},
														].map((item) => (
															<motion.div
																key={item.href}
																whileHover={{ x: 4 }}
																whileTap={{ scale: 0.98 }}
															>
																<Link
																	href={item.href}
																	className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/80 hover:text-white hover:bg-primary/20 transition-colors"
																	onClick={closeMenu}
																>
																	<svg
																		xmlns="http://www.w3.org/2000/svg"
																		fill="none"
																		viewBox="0 0 24 24"
																		strokeWidth={1.5}
																		stroke="currentColor"
																		className="h-4 w-4 text-primary"
																	>
																		<path
																			strokeLinecap="round"
																			strokeLinejoin="round"
																			d={item.icon}
																		/>
																	</svg>
																	{item.label}
																</Link>
															</motion.div>
														))}
														{isAdmin && (
															<motion.div
																whileHover={{ x: 4 }}
																whileTap={{ scale: 0.98 }}
															>
																<Link
																	href="/admin"
																	className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/80 hover:text-white hover:bg-primary/20 transition-colors"
																	onClick={closeMenu}
																>
																	<svg
																		xmlns="http://www.w3.org/2000/svg"
																		fill="none"
																		viewBox="0 0 24 24"
																		strokeWidth={1.5}
																		stroke="currentColor"
																		className="h-4 w-4 text-primary"
																	>
																		<path
																			strokeLinecap="round"
																			strokeLinejoin="round"
																			d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V19.5a2.25 2.25 0 002.25 2.25h.75m0-3H12m-.75 3h3.75m-3.75 0V19.5m0 3h3.75M9 6.75h3m-3 3h3m-3 3h3m-3 3h3m-3 3h3m-3 3h3"
																		/>
																	</svg>
																	Admin Dashboard
																</Link>
															</motion.div>
														)}
													</div>

													<div className="border-t border-primary/20 p-2">
														<motion.button
															whileHover={{ scale: 1.02 }}
															whileTap={{ scale: 0.98 }}
															onClick={() => {
																closeMenu();
																signOut({ callbackUrl: "/" });
															}}
															className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
														>
															<svg
																xmlns="http://www.w3.org/2000/svg"
																fill="none"
																viewBox="0 0 24 24"
																strokeWidth={1.5}
																stroke="currentColor"
																className="h-4 w-4"
															>
																<path
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
																/>
															</svg>
															Sign out
														</motion.button>
													</div>
												</motion.div>
											)}
										</AnimatePresence>
									</div>
								</>
							) : (
								<motion.div
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
								>
									<Link
										href="/auth"
										className="inline-flex h-10 items-center justify-center rounded-full bg-gradient-to-r from-primary to-purple-light px-5 py-2 text-sm font-medium text-white shadow-lg shadow-primary/20 transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 hover:from-primary/90 hover:to-purple-light/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50"
									>
										Sign In
									</Link>
								</motion.div>
							)}
						</div>

						{/* Mobile menu toggle button */}
						<motion.button
							ref={mobileMenuButtonRef}
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
							className="inline-flex items-center justify-center rounded-full p-2 text-sm font-medium transition-colors hover:bg-primary/20 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50 md:flex lg:hidden z-50"
							onClick={handleMobileMenuToggle}
							aria-label={activeMenu === "mobile" ? "Close menu" : "Open menu"}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="h-6 w-6"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d={
										activeMenu === "mobile"
											? "M6 18L18 6M6 6l12 12"
											: "M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
									}
								/>
							</svg>
							<span className="sr-only">
								{activeMenu === "mobile" ? "Close Menu" : "Open Menu"}
							</span>
						</motion.button>
					</div>
				</div>
			</motion.header>

			{/* Mobile Menu with clickable backdrop and animations */}
			<AnimatePresence>
				{activeMenu === "mobile" && (
					<motion.div
						ref={mobileMenuRef}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						className="fixed inset-x-0 bottom-0 z-40 lg:hidden"
						style={{ top: "80px" }}
						onClick={handleBackdropClick}
					>
						{/* Backdrop - clickable to close menu */}
						<motion.div
							className="absolute inset-0 bg-purple-dark/95 backdrop-blur-xl md:hidden"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						/>

						{/* Content container - clicks inside won't close the menu */}
						<div className="relative p-4">
							<motion.div
								ref={mobileMenuContentRef}
								initial={{ scale: 0.95, opacity: 0, y: -20 }}
								animate={{ scale: 1, opacity: 1, y: 0 }}
								exit={{ scale: 0.95, opacity: 0, y: -20 }}
								transition={{
									type: "spring",
									stiffness: 300,
									damping: 30,
									opacity: { duration: 0.2 },
								}}
								className={cn(
									"mx-auto max-w-md rounded-xl border border-primary/20 p-4 shadow-lg shadow-primary/10",
									isEdgeBrowser
										? "bg-purple-dark"
										: "bg-purple-dark/95 backdrop-blur-xl",
									"md:absolute md:right-5 md:top-2 md:mx-0 md:w-72"
								)}
							>
								<nav className="grid gap-2 text-sm">
									{mainNavigation.map((item) => (
										<motion.div
											key={item.href}
											whileHover={{ x: 4 }}
											whileTap={{ scale: 0.98 }}
										>
											<Link
												href={item.href}
												className={cn(
													"flex w-full items-center gap-3 rounded-lg p-3 text-sm font-medium transition-colors",
													pathname === item.href
														? "bg-primary/20 text-white"
														: "text-white/80 hover:bg-primary/10 hover:text-white"
												)}
												onClick={closeMenu}
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 24 24"
													strokeWidth={1.5}
													stroke="currentColor"
													className="w-5 h-5"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d={item.icon}
													/>
												</svg>
												{item.name}
											</Link>
										</motion.div>
									))}
									{session ? (
										<>
											<div className="md:hidden">
												{/* Mobile Balance Display */}
												<div className="flex flex-col gap-1 px-3 py-2">
													<div className="flex items-center justify-between">
														<span className="text-xs text-white/60">
															Available Balance
														</span>
														<Link
															href="/dashboard/balance"
															className="text-xs text-primary hover:text-primary/80 transition-colors"
															onClick={closeMenu}
														>
															Add Balance
														</Link>
													</div>
													<div className="flex items-center gap-2">
														<div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
															<svg
																xmlns="http://www.w3.org/2000/svg"
																fill="none"
																viewBox="0 0 24 24"
																strokeWidth={1.5}
																stroke="currentColor"
																className="w-4 h-4 text-primary"
															>
																<path
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
																/>
															</svg>
														</div>
														<div className="flex flex-col">
															<span className="text-lg font-semibold text-white">
																{formatCurrency(balance)}
															</span>
														</div>
													</div>
												</div>

												<div className="my-2 h-px bg-primary/20" />

												{[
													{ href: "/dashboard", label: "Dashboard" },
													{
														href: "/dashboard/inventory",
														label: "My Inventory",
													},
													{
														href: "/dashboard/balance",
														label: "Add Balance",
													},
												].map((item) => (
													<motion.div
														key={item.href}
														whileHover={{ x: 4 }}
														whileTap={{ scale: 0.98 }}
													>
														<Link
															href={item.href}
															className="flex w-full items-center rounded-lg p-3 text-sm font-medium text-white/80 hover:bg-primary/10 hover:text-white transition-colors"
															onClick={closeMenu}
														>
															{item.label}
														</Link>
													</motion.div>
												))}
												{isAdmin && (
													<motion.div
														whileHover={{ x: 4 }}
														whileTap={{ scale: 0.98 }}
													>
														<Link
															href="/admin"
															className="flex w-full items-center rounded-lg p-3 text-sm font-medium text-white/80 hover:bg-primary/10 hover:text-white transition-colors"
															onClick={closeMenu}
														>
															Admin Dashboard
														</Link>
													</motion.div>
												)}

												<div className="my-2 h-px bg-primary/20" />
												<motion.button
													whileHover={{ scale: 1.02 }}
													whileTap={{ scale: 0.98 }}
													onClick={() => {
														closeMenu();
														signOut({ callbackUrl: "/" });
													}}
													className="flex w-full items-center rounded-lg p-3 text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors"
												>
													Sign out
												</motion.button>
											</div>
										</>
									) : (
										<motion.div
											whileHover={{ scale: 1.02 }}
											whileTap={{ scale: 0.98 }}
										>
											<Link
												href="/auth"
												className="flex w-full items-center rounded-lg p-3 text-sm font-medium text-white/80 hover:bg-primary/10 hover:text-white transition-colors"
												onClick={closeMenu}
											>
												Sign In
											</Link>
										</motion.div>
									)}
								</nav>
							</motion.div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
};

export default Header;
